import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, TemplateRef, ChangeDetectorRef } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { Subject, debounceTime } from 'rxjs';
import dayjs from '../../../services/dayjs-setup';
import { AppContextService } from '../../../services/app-context.service';
import { ToasterService } from '../../../shared-components/custom-toaster/toaster.service';
import { CurrencyLocalePipe } from '../../../services/currency-locale.pipe';
import { DateLocalePipe } from '../../../services/date-locale.pipe';
import { html } from '../../../utils/html-tag-utils';
import {
	TableColumn,
	TableConfig,
	TableExportOption,
	TableFilter,
	TableRow,
	TableSearchEvent,
	TableSelectionEvent,
} from '../../../shared-components/custom-table/custom-table.types';
import {
	TableSelectAction,
	TableToolbarAction,
} from '../../../shared-components/custom-table/components/table-toolbar/table-toolbar.component';
import { CustomTableComponent } from '../../../shared-components/custom-table/custom-table.component';
import { TablePillCellComponent } from './components/table-pill-cell.component';

interface ShowcaseCompany {
	Name: string;
	CountryCode: string;
	VatNumber: string;
}

interface ShowcaseRow extends TableRow {
	Id: string;
	Type: string;
	Number: string;
	Supplier: ShowcaseCompany;
	Buyer: ShowcaseCompany;
	TotalAmount: number;
	Currency: string;
	CreationDate: string;
	DueDate: string;
	ApprovalState: string;
	PaymentState: string;
}

const DOC_TYPES = ['Invoice', 'Credit Note', 'Debit Note'];
const APPROVAL_STATES = ['Approved', 'Pending', 'Rejected', 'NotSubmitted'];
const PAYMENT_STATES = ['Unpaid', 'Paid', 'Advanced', 'Pending'];
const CURRENCIES = ['EUR', 'USD', 'GBP'];

const APPROVAL_VARIANTS: { [value: string]: string } = {
	Approved: 'success',
	Pending: 'warning',
	Rejected: 'error',
	NotSubmitted: 'neutral',
};

const PAYMENT_VARIANTS: { [value: string]: string } = {
	Unpaid: 'neutral',
	Paid: 'success',
	Advanced: 'info',
	Pending: 'warning',
};

const SUPPLIERS: ShowcaseCompany[] = [
	{ Name: 'Acme Supplies Ltd', CountryCode: 'PT', VatNumber: '501234567' },
	{ Name: 'Northwind Traders', CountryCode: 'ES', VatNumber: 'B12345678' },
	{ Name: 'Globex Corporation', CountryCode: 'DE', VatNumber: '123456789' },
	{ Name: 'Initech GmbH', CountryCode: 'DE', VatNumber: '987654321' },
	{ Name: 'Umbrella Logistics', CountryCode: 'FR', VatNumber: '44123456789' },
	{ Name: 'Stark Industries', CountryCode: 'US', VatNumber: '88-1234567' },
	{ Name: 'Wayne Enterprises', CountryCode: 'GB', VatNumber: '123456789' },
	{ Name: 'Hooli Inc', CountryCode: 'US', VatNumber: '77-7654321' },
	{ Name: 'Soylent Corp', CountryCode: 'NL', VatNumber: '851234567B01' },
	{ Name: 'Tyrell Corporation', CountryCode: 'ES', VatNumber: 'A87654321' },
	{ Name: 'Wonka Industries', CountryCode: 'GB', VatNumber: '987654321' },
	{ Name: 'Cyberdyne Systems', CountryCode: 'PT', VatNumber: '509876543' },
];

const BUYERS: ShowcaseCompany[] = [
	{ Name: 'Default Buyer SA', CountryCode: 'PT', VatNumber: '505111222' },
	{ Name: 'Gringotts Bank', CountryCode: 'GB', VatNumber: '555444333' },
	{ Name: 'Oscorp Retail', CountryCode: 'US', VatNumber: '55-1112223' },
	{ Name: 'Vehement Capital', CountryCode: 'DE', VatNumber: '555666777' },
	{ Name: 'Aperture Science', CountryCode: 'FR', VatNumber: '44998877665' },
	{ Name: 'Bluth Company', CountryCode: 'US', VatNumber: '66-4445556' },
	{ Name: 'Prestige Worldwide', CountryCode: 'ES', VatNumber: 'B55566677' },
	{ Name: 'Dunder Mifflin', CountryCode: 'US', VatNumber: '33-2221110' },
	{ Name: 'Pied Piper', CountryCode: 'NL', VatNumber: '859988776B02' },
	{ Name: 'Nakatomi Trading', CountryCode: 'PT', VatNumber: '503334445' },
];

const DEFAULT_ROW_COUNT = 137;

@Component({
	selector: 'app-custom-table-showcase',
	templateUrl: './custom-table-showcase.component.html',
	styleUrls: ['./custom-table-showcase.component.scss'],
	standalone: false,
})
export class CustomTableShowcaseComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('customTable') customTable: CustomTableComponent;
	@ViewChild('processSelectedButton') processSelectedButton: TemplateRef<any>;
	@ViewChild('newDocumentButton') newDocumentButton: TemplateRef<any>;

	public rows: ShowcaseRow[] = [];
	public rowCount: string = String(DEFAULT_ROW_COUNT);
	public readonly rowCountPresets: number[] = [0, 51, DEFAULT_ROW_COUNT, 10000, 100000];

	public isLoading = false;
	public cardViewOnly = false;

	public selectActions: TableSelectAction[] = [];
	public toolbarActions: TableToolbarAction[] = [];
	public selectedRows: ShowcaseRow[] = [];

	public tableConfig: TableConfig = {
		initialPageSize: 20,
		pageSizeOptions: [20, 50, 100],
	};

	private regenerate$ = new Subject<number>();

	constructor(
		private toasterService: ToasterService,
		private appContext: AppContextService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.regenerate$.pipe(debounceTime(400)).subscribe((count) => this.generateRows(count));
		this.generateRows(DEFAULT_ROW_COUNT);
	}

	ngAfterViewInit(): void {
		// Shown in the toolbar while at least one row is selected
		this.selectActions = [{ template: this.processSelectedButton }];
		// Shown in the toolbar while nothing is selected
		this.toolbarActions = [{ template: this.newDocumentButton }];
		this.cdr.detectChanges();
	}

	ngOnDestroy(): void {
		this.regenerate$.complete();
	}

	// ------------------------------------------------------------------
	// Mock data generation - deterministic, all fields derived from index
	// ------------------------------------------------------------------

	public generateRows(count: number): void {
		const safeCount = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
		this.rows = Array.from({ length: safeCount }, (_, i) => this.buildRow(i));
		this.rowCount = String(safeCount);
		this.selectedRows = [];
		// New dataset = new identities; drop any persisted selection so the count stays honest
		this.customTable?.clearSelection();
		console.log(`[table-showcase] generated ${safeCount} rows`);
	}

	private buildRow(index: number): ShowcaseRow {
		const issueDate = dayjs().subtract((index * 3) % 540, 'day');
		return {
			Id: `showcase-row-${index}`,
			Type: DOC_TYPES[index % DOC_TYPES.length],
			Number: `DOC-${String(index + 1).padStart(6, '0')}`,
			Supplier: SUPPLIERS[index % SUPPLIERS.length],
			Buyer: BUYERS[(index * 3 + 1) % BUYERS.length],
			TotalAmount: Math.round((((index * 1234.5678) % 49500) + 500) * 100) / 100,
			Currency: CURRENCIES[index % CURRENCIES.length],
			CreationDate: issueDate.toISOString(),
			DueDate: issueDate.add(((index % 6) + 1) * 15, 'day').toISOString(),
			ApprovalState: APPROVAL_STATES[index % APPROVAL_STATES.length],
			PaymentState: PAYMENT_STATES[index % PAYMENT_STATES.length],
			canSelect: index % 7 !== 0,
		};
	}

	onRowCountChange(value: string): void {
		// Ignore empty/invalid input so clearing the field doesn't wipe the table mid-typing
		if (value === null || value === undefined || value === '') return;
		const parsed = parseInt(value, 10);
		if (!Number.isNaN(parsed)) this.regenerate$.next(parsed);
	}

	setRowCount(count: number): void {
		this.generateRows(count);
	}

	// ------------------------------------------------------------------
	// Columns - function renders, component renders, icons & action menu
	// ------------------------------------------------------------------

	public tableColumns: TableColumn[] = [
		{
			field: 'Number',
			header: 'Document',
			sortable: true,
			render: (row: ShowcaseRow) => {
				return html`
					<p>${row.Type}</p>
					<span class="is-bold">${row.Number}</span>
				`;
			},
			tooltip: (row: ShowcaseRow) => `${row.Type} ${row.Number}`,
		},
		{
			field: 'Supplier',
			header: 'Supplier',
			render: (row: ShowcaseRow) => {
				return html`
					<p class="truncate">${row.Supplier?.Name}</p>
					<span>${row.Supplier?.CountryCode}${row.Supplier?.VatNumber}</span>
				`;
			},
			tooltip: (row: ShowcaseRow) => `${row.Supplier?.Name} ${row.Supplier?.CountryCode}${row.Supplier?.VatNumber}`,
		},
		{
			field: 'Buyer',
			header: 'Buyer',
			render: (row: ShowcaseRow) => {
				return html`
					<p class="truncate">${row.Buyer?.Name}</p>
					<span>${row.Buyer?.CountryCode}${row.Buyer?.VatNumber}</span>
				`;
			},
			tooltip: (row: ShowcaseRow) => `${row.Buyer?.Name} ${row.Buyer?.CountryCode}${row.Buyer?.VatNumber}`,
		},
		{
			field: 'TotalAmount',
			header: 'Amount',
			align: 'right',
			sortable: true,
			render: (row: ShowcaseRow) => {
				const currencyPipe = new CurrencyLocalePipe(new CurrencyPipe(this.appContext.getCurrentLanguage()));
				return html`<span class="is-bold">${currencyPipe.transform(row.TotalAmount, row.Currency)}</span>`;
			},
		},
		{
			field: 'CreationDate',
			header: 'Issued',
			sortable: true,
			render: (row: ShowcaseRow) => {
				const datePipe = new DateLocalePipe(this.appContext);
				return html`<span>${datePipe.transform(row.CreationDate, 'yyyy/MM/dd')}</span>`;
			},
		},
		{
			field: 'DueDate',
			header: 'Due date',
			sortable: true,
			render: (row: ShowcaseRow) => {
				const datePipe = new DateLocalePipe(this.appContext);
				return html`<span>${datePipe.transform(row.DueDate, 'yyyy/MM/dd')}</span>`;
			},
		},
		{
			field: 'ApprovalState',
			header: 'Approval',
			align: 'center',
			sortable: true,
			render: TablePillCellComponent,
			inputs: () => ({ field: 'ApprovalState', variantMap: APPROVAL_VARIANTS }),
		},
		{
			field: 'PaymentState',
			header: 'Payment',
			align: 'center',
			render: TablePillCellComponent,
			inputs: () => ({ field: 'PaymentState', variantMap: PAYMENT_VARIANTS }),
		},
		{
			type: 'icons',
			iconList: [
				{
					icon: 'fa-solid fa-triangle-exclamation',
					className: 'is-color-danger',
					tooltip: 'Overdue - due date has passed and it is still unpaid',
					condition: (row: ShowcaseRow) => this.isOverdue(row),
					action: (row: ShowcaseRow) => this.flagOverdue(row),
				},
				{
					icon: 'fa-solid fa-star',
					className: 'is-color-warning',
					tooltip: 'High amount (>= 25,000)',
					condition: (row: ShowcaseRow) => row.TotalAmount >= 25000,
				},
			],
		},
		{
			type: 'actions',
			icon: 'fa-solid fa-ellipsis-v',
			isMenu: true,
			menuItems: [
				{
					icon: 'fa-regular fa-eye',
					label: 'View details',
					action: (row: ShowcaseRow) => this.viewDetails(row),
				},
				{
					icon: 'fa-regular fa-arrow-down-to-line',
					label: 'Download',
					action: (row: ShowcaseRow) => this.downloadDocument(row),
				},
				{ isSeparator: true },
				{
					isExpand: true,
					icon: 'fa-regular fa-ellipsis-h',
					label: (_row: ShowcaseRow, expandState?: boolean) => (expandState ? 'Hide summary' : 'Show summary'),
				},
			],
		},
	];

	// ------------------------------------------------------------------
	// Filters - all applied client-side by the table
	// ------------------------------------------------------------------

	public tableFilters: TableFilter[] = [
		{
			field: 'Number',
			type: 'text',
			value: null,
			placeholder: 'Document number',
		},
		{
			field: 'Type',
			type: 'select',
			value: null,
			placeholder: 'Document type',
			options: DOC_TYPES.map((type) => ({ value: type, label: type })),
			filterFn: (row: ShowcaseRow, value: any) => {
				const selected = Array.isArray(value) ? value : value ? [value] : [];
				return selected.length === 0 || selected.includes(row.Type);
			},
		},
		{
			field: 'Supplier',
			type: 'select',
			value: null,
			placeholder: 'Supplier',
			options: SUPPLIERS.map((s) => ({ value: s.Name, label: `${s.Name} (${s.CountryCode}${s.VatNumber})` })),
			filterFn: (row: ShowcaseRow, value: any) => {
				const selected = Array.isArray(value) ? value : value ? [value] : [];
				return selected.length === 0 || selected.includes(row.Supplier?.Name);
			},
		},
		{
			field: 'ApprovalState',
			type: 'singleSelect',
			value: null,
			placeholder: 'Approval state',
			options: APPROVAL_STATES.map((state) => ({ value: state, label: state })),
			filterFn: (row: ShowcaseRow, value: any) => !value || row.ApprovalState === value,
		},
		{
			field: 'PaymentState',
			type: 'select',
			value: null,
			placeholder: 'Payment state',
			options: PAYMENT_STATES.map((state) => ({ value: state, label: state })),
			filterFn: (row: ShowcaseRow, value: any) => {
				const selected = Array.isArray(value) ? value : value ? [value] : [];
				return selected.length === 0 || selected.includes(row.PaymentState);
			},
		},
		{
			field: 'TotalAmount',
			type: 'number',
			value: null,
			placeholder: 'Min amount',
			filterFn: (row: ShowcaseRow, value: any) => {
				if (value === null || value === undefined || value === '') return true;
				return row.TotalAmount >= Number(value);
			},
		},
		{
			field: 'CreationDate',
			type: 'date-picker',
			value: null,
			placeholder: 'Issued between',
			metadata: {
				datePickerConfig: {
					calendars: 2,
					isRange: true,
					format: 'YYYY/MM/DD',
				},
			},
			filterFn: (row: ShowcaseRow, value: any) => {
				if (!value) return true;
				const start = Array.isArray(value) ? value[0] : value.startDate;
				const end = Array.isArray(value) ? value[1] : value.endDate;
				if (!start && !end) return true;
				const date = dayjs(row.CreationDate);
				if (start && date.isBefore(dayjs(start), 'day')) return false;
				if (end && date.isAfter(dayjs(end), 'day')) return false;
				return true;
			},
		},
	];

	// ------------------------------------------------------------------
	// Export / events - everything surfaces through the toaster + console
	// ------------------------------------------------------------------

	public tableExport: TableExportOption = {
		label: 'button.export',
		icon: 'fa-regular fa-arrow-down-to-line',
		action: () => this.exportRows(),
		condition: () => this.rows.length > 0,
	};

	exportRows(): void {
		this.toasterService.showInfo(`Export requested for ${this.rows.length} rows`);
		console.log('[table-showcase] export rows', this.rows);
		this.tableExport = { ...this.tableExport, exporting: false };
	}

	processSelected(): void {
		this.toasterService.showSuccess(`${this.selectedRows.length} row(s) processed`);
		console.log('[table-showcase] process selected', this.selectedRows);
	}

	createDocument(): void {
		this.toasterService.showInfo('New document clicked');
		console.log('[table-showcase] new document');
	}

	viewDetails(row: ShowcaseRow): void {
		this.toasterService.showInfo(`Viewing ${row.Number}`);
		console.log('[table-showcase] view details', row);
	}

	downloadDocument(row: ShowcaseRow): void {
		this.toasterService.showInfo(`Download requested for ${row.Number}`);
		console.log('[table-showcase] download', row);
	}

	flagOverdue(row: ShowcaseRow): void {
		this.toasterService.showWarning(`${row.Number} is overdue and unpaid`);
		console.log('[table-showcase] overdue flag clicked', row);
	}

	isOverdue(row: ShowcaseRow): boolean {
		return row.PaymentState === 'Unpaid' && dayjs(row.DueDate).isBefore(dayjs(), 'day');
	}

	// Same variant mapping used by TablePillCellComponent, reused in the card template
	approvalVariant(row: ShowcaseRow): string {
		return APPROVAL_VARIANTS[row.ApprovalState] ?? 'neutral';
	}

	paymentVariant(row: ShowcaseRow): string {
		return PAYMENT_VARIANTS[row.PaymentState] ?? 'neutral';
	}

	onSearchRequested(event: TableSearchEvent): void {
		console.log('[table-showcase] searchRequested', event);
	}

	onSelectionChange(event: TableSelectionEvent): void {
		this.selectedRows = (event?.allSelectedRows ?? []) as ShowcaseRow[];
		console.log('[table-showcase] selectionChange', event);
	}
}
